import { Col, Row } from 'antd';
import { type FormikHelpers, useFormikContext } from 'formik';
import { isNil } from 'lodash/fp';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import {
    type ApplicationJourneyDepositFragment,
    CompanyTheme,
    type ConfiguratorApplicationEntrypointContextDataFragment,
    CustomerKind,
    type KycFieldSpecsFragment,
    LocalCustomerFieldKey,
    ModuleType,
} from '../../../../api';
import { useAccount } from '../../../../components/contexts/AccountContextManager';
import Form from '../../../../components/fields/Form';
import FormAutoTouch from '../../../../components/FormAutoTouch';
import { useThemeComponents } from '../../../../themes';
import { type UploadDocumentProp, getInitialValues } from '../../../../utilities/kycPresets';
import { type LocalVariant } from '../../../../utilities/useVehicleOptions';
import { hasAppointmentScenario } from '../../../admin/ModuleDetailsPage/modules/implementations/shared';
import CustomerDetails from '../../../shared/JourneyPage/CustomerDetails';
import AffinAutoFinanceCentreSelect from '../../../shared/JourneyPage/CustomerDetails/AffinAutoFinanceCentreSelect';
import ResetKYCButtonPorsche from '../../../shared/JourneyPage/CustomerDetails/ResetKYCButtonPorsche';
import JourneySectionWrapper from '../../../shared/JourneyPage/JourneySectionWrapper';
import { QuotationDetails } from '../../../shared/JourneyPage/QuotationDetails';
import ProceedWithCustomerButton from '../../../shared/ProceedWithCustomerButton';
import AppointmentDetailsSection from '../../EventApplicationEntrypoint/ApplicantForm/AppointmentDetailsSection';
import { type State } from '../../StandardApplicationEntrypoint/Journey/reducer';
import { SectionDivider } from '../../StandardApplicationEntrypoint/KYCPage/shared';
import { NextButton } from '../../StandardApplicationEntrypoint/shared/JourneyButton';
import { Backdrop, StyledJourneyToolbar } from '../../StandardApplicationEntrypoint/styledComponents';
import { type ConfiguratorApplicationState } from '../Journey/JourneyController';
import { usePersistConfiguratorJourneyValues } from '../usePersistConfiguratorJourneyValues';
import ConsentsAndDeclarations from './ConsentsAndDeclarations';
import { useMyInfoAndOcrContent } from './MyInfoContent';
import { type AgreementAndKycStateData, type Agreements, type ApplicantFormValues } from './types';
import VehicleInterest from './VehicleInterest';

const leftColSpan = { xl: 8, lg: 12, md: 24 };
const rightColSpan = { xl: 16, lg: 12, md: 24 };

export type KYCPageInnerProps = {
    configurator: ConfiguratorApplicationState;
    variants: LocalVariant[];
    kycPresets: KycFieldSpecsFragment[];
    applicationAgreements: Agreements;
    proceedWithCustomer: (values: ApplicantFormValues, helpers: FormikHelpers<ApplicantFormValues>) => void;
    state?: State<ConfiguratorApplicationState>;
    endpoint: ConfiguratorApplicationEntrypointContextDataFragment;
    deposit: ApplicationJourneyDepositFragment;
    setIsCorporate?: Dispatch<SetStateAction<boolean>>;
    customerKind: CustomerKind;
    showTabs: boolean;
    hasGuarantorPreset: boolean;
    setPrefill: Dispatch<SetStateAction<boolean>>;
    showResetButton?: boolean;
    setAgreementAndKyc?: Dispatch<SetStateAction<AgreementAndKycStateData>>;
    token?: string;
} & UploadDocumentProp;

const KYCPageInner = ({
    configurator,
    kycPresets,
    applicationAgreements,
    state,
    proceedWithCustomer,
    endpoint,
    deposit,
    setIsCorporate,
    customerKind,
    hasGuarantorPreset,
    showTabs,
    setPrefill,
    showResetButton = false,
    uploadDocument,
    removeDocument,
}: KYCPageInnerProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const application = state?.application;
    const { values, isSubmitting, handleSubmit, ...rest } = useFormikContext<ApplicantFormValues>();
    const { resetForm, initialValues, validateForm, submitForm } = rest;

    const { save: persistConfiguratorJourneyValue, persistedValue } = usePersistConfiguratorJourneyValues();

    const { theme } = useThemeComponents();
    const myinfoContent = useMyInfoAndOcrContent();
    const user = useAccount(true);

    useEffect(() => {
        persistConfiguratorJourneyValue({
            ...persistedValue,
            kyc: values,
        });
    }, [values]);

    const showRemoteFlowButtonInKYCPage = useMemo(() => {
        switch (configurator.module.__typename) {
            case ModuleType.ConfiguratorModule:
                return configurator.module.showRemoteFlowButtonInKYCPage;

            default:
                return null;
        }
    }, [configurator.module]);

    const proceedWithCustomerButton = useMemo((): boolean => {
        const keys = new Set(kycPresets.map(preset => preset.key));

        if (
            (keys.has(LocalCustomerFieldKey.FirstName) ||
                keys.has(LocalCustomerFieldKey.LastName) ||
                keys.has(LocalCustomerFieldKey.FullName)) &&
            keys.has(LocalCustomerFieldKey.Email) &&
            keys.has(LocalCustomerFieldKey.IdentityNumber)
        ) {
            return showRemoteFlowButtonInKYCPage && user && !application.withCustomerDevice;
        }

        return false;
    }, [kycPresets, showRemoteFlowButtonInKYCPage, user, application.withCustomerDevice]);

    const isApplyingFromDetails = useMemo(
        () =>
            !isNil(application.draftFlow.isApplyingForFinanceCompleted) ||
            !isNil(application.draftFlow.isApplyingForInsuranceCompleted) ||
            !isNil(application.draftFlow.isApplyingForReservationCompleted),
        [
            application.draftFlow.isApplyingForFinanceCompleted,
            application.draftFlow.isApplyingForInsuranceCompleted,
            application.draftFlow.isApplyingForReservationCompleted,
        ]
    );

    const isApplyingFromApplyNew = useMemo(
        () =>
            application.draftFlow.isReceived &&
            (!application.draftFlow.isApplyNewForFinancingReceived ||
                !application.draftFlow.isApplyNewForInsuranceReceived ||
                !application.draftFlow.isApplyNewForAppointmentReceived),
        [
            application.draftFlow.isReceived,
            application.draftFlow.isApplyNewForAppointmentReceived,
            application.draftFlow.isApplyNewForFinancingReceived,
            application.draftFlow.isApplyNewForInsuranceReceived,
        ]
    );

    const isAppointmentSectionVisible = useMemo(
        () =>
            customerKind !== CustomerKind.Guarantor &&
            values.configuration.testDrive &&
            hasAppointmentScenario(endpoint.configuratorApplicationModule.scenarios) &&
            endpoint.configuratorApplicationModule.appointmentModule &&
            endpoint.configuratorApplicationModule.displayAppointmentDatepicker,
        [
            customerKind,
            endpoint.configuratorApplicationModule.appointmentModule,
            endpoint.configuratorApplicationModule.displayAppointmentDatepicker,
            endpoint.configuratorApplicationModule.scenarios,
            values.configuration.testDrive,
        ]
    );

    const resetFormHandler = useCallback(() => {
        resetForm({
            values: {
                ...initialValues,
                customer: { fields: getInitialValues([], kycPresets) },
                agreements: { ...values.agreements },
            },
        });
    }, [initialValues, kycPresets, resetForm, values.agreements]);

    const sectionDivider = useMemo(
        () =>
            application.configuration.isAffinAutoFinanceCentreRequired || isAppointmentSectionVisible ? (
                <Col span={24}>
                    <SectionDivider />
                </Col>
            ) : null,
        [application.configuration.isAffinAutoFinanceCentreRequired, isAppointmentSectionVisible]
    );

    const onSubmit = useCallback(async () => {
        await validateForm();
        await submitForm();
    }, [submitForm, validateForm]);
    const showCommentsToBank = user && configurator?.bank?.showCommentsField;

    const { insurers } = endpoint.configuratorApplicationModule;
    const showCommentsToInsurer = useMemo(() => {
        if (!user) {
            return false;
        }

        const insurer = insurers.find(insurer => insurer.id === application?.insurancing?.insurerId);

        return insurer?.showCommentsField;
    }, [user, insurers, application?.insurancing?.insurerId]);

    return (
        <>
            <FormAutoTouch />
            <Form id="applicantForm" name="applicantForm" onSubmitCapture={handleSubmit}>
                <Row gutter={[24, 50]}>
                    <Col {...leftColSpan}>
                        <Row gutter={[50, 40]}>
                            <Col span={24}>
                                <VehicleInterest configurator={configurator} />
                            </Col>
                            {application.configuration.isAffinAutoFinanceCentreRequired && (
                                <Col span={24}>
                                    <AffinAutoFinanceCentreSelect />
                                </Col>
                            )}
                            {isAppointmentSectionVisible && (
                                <Col span={24}>
                                    <AppointmentDetailsSection
                                        application={application}
                                        applicationModule={endpoint.configuratorApplicationModule}
                                        showSkipValidation={false}
                                    />
                                </Col>
                            )}

                            {myinfoContent.render({
                                configurator,
                                endpoint,
                                token: state.token,
                                onLoading: setIsLoading,
                                kycPresets,
                                topDivider: sectionDivider,
                            })}
                        </Row>
                    </Col>
                    <Col {...rightColSpan}>
                        <JourneySectionWrapper
                            applicationType={application.__typename}
                            extra={
                                proceedWithCustomerButton && (
                                    <ProceedWithCustomerButton onClick={() => proceedWithCustomer(values, rest)} />
                                )
                            }
                            stage={state.stage}
                            stages={state.stages}
                        >
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <CustomerDetails
                                        customerKind={customerKind}
                                        hasGuarantorPreset={hasGuarantorPreset}
                                        hasUploadDocuments={application.bank?.hasUploadDocuments}
                                        hasVSOUpload={false}
                                        isApplyingFromApplyNew={isApplyingFromApplyNew}
                                        isApplyingFromDetails={isApplyingFromDetails}
                                        isGuarantorCompleted={application.draftFlow.isGuarantorCompleted}
                                        kycPresets={kycPresets}
                                        removeDocument={removeDocument}
                                        resetFormHandler={resetFormHandler}
                                        setIsCorporate={setIsCorporate}
                                        setPrefill={setPrefill}
                                        showCommentsToInsurer={showCommentsToInsurer}
                                        showRemarks={showCommentsToBank}
                                        showResetButton={showResetButton && !myinfoContent.withMyInfo}
                                        showTabs={showTabs}
                                        showTitle={false}
                                        uploadDocument={uploadDocument}
                                        withFinancing={application.configuration.withFinancing}
                                    />
                                </Col>

                                <Col span={24}>
                                    <ConsentsAndDeclarations applicationAgreements={applicationAgreements} />
                                </Col>
                                {values.quotation?.source && (
                                    <Col span={24}>
                                        <QuotationDetails source={values.quotation?.source} />
                                    </Col>
                                )}
                            </Row>
                        </JourneySectionWrapper>
                    </Col>
                </Row>
            </Form>
            <StyledJourneyToolbar>
                {showResetButton &&
                    !myinfoContent.withMyInfo &&
                    (theme === CompanyTheme.Porsche || theme === CompanyTheme.PorscheV3) && (
                        <ResetKYCButtonPorsche onConfirm={resetFormHandler} />
                    )}
                <NextButton disabled={isSubmitting} form="applicantForm" onSubmit={onSubmit} />
            </StyledJourneyToolbar>
            {isLoading && <Backdrop />}
        </>
    );
};

export default KYCPageInner;
